import React, { useEffect, useState } from 'react';
import useUrlParams from '../../hooks/useUrlParams';
import { color, font, space } from '../../theme';
import { Card } from '../Card';
import Chip from '../Chip';
import Button from '../Button';
import { EmptyState, ErrorState } from '../States';
import { SkeletonList } from '../Skeleton';
import AgendaMotionRow from './AgendaMotionRow';
import { AgendaMotionData, loadAgendaMotionData, MOTION_TYPE_SHORT } from './agendaMotions';

const PAGE = 20;
// The type filter in the URL (?amType=), so Back from an MK page keeps it
const TYPE_PARAM = 'amType';

/* A committee's motions for the agenda (5b): the motions referred to it for discussion, in
   the committee page's פעילות tab. Chips filter by type (the committee's types, most
   common first); rows open in place. `knessetNumber`: the committee's Knesset, whose list
   (live or archived) is read. */
export default function CommitteeAgendaMotions({ committeeId, knessetNumber }: { committeeId: string; knessetNumber: number }) {
  const [params, setParams] = useUrlParams();
  const [data, setData] = useState<AgendaMotionData | null>(null);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [visibleCount, setVisibleCount] = useState(PAGE);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    setData(null);
    loadAgendaMotionData(knessetNumber)
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [knessetNumber, reloadKey]);

  if (error) return <Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card>;
  if (!data) return <SkeletonList rows={5} />;

  const motions = data.motions.filter(m => m.committeeId === committeeId);
  if (motions.length === 0) {
    return <Card padding={0}><EmptyState icon="document" title="אין הצעות לסדר היום" body="לא הועברו לוועדה הצעות לסדר היום לדיון." /></Card>;
  }

  const counts: Record<string, number> = {};
  motions.forEach(m => { const t = m.type || 'אחר'; counts[t] = (counts[t] || 0) + 1; });
  const types = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  const typeParam = params.get(TYPE_PARAM);
  const type = typeParam && counts[typeParam] ? typeParam : null;
  const setType = (t: string | null) => {
    setVisibleCount(PAGE);
    setParams(prev => {
      const next = new URLSearchParams(prev);
      if (t) next.set(TYPE_PARAM, t); else next.delete(TYPE_PARAM);
      return next;
    });
  };
  const shown = type ? motions.filter(m => (m.type || 'אחר') === type) : motions;
  const visible = shown.slice(0, visibleCount);
  const from = data.committees[committeeId] || 'הוועדה';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
      {types.length > 1 && (
        <div role="group" aria-label="סוג הצעה" style={{ display: 'flex', flexWrap: 'wrap', columnGap: space.sm }}>
          <Chip selected={!type} onClick={() => setType(null)} count={motions.length}>הכל</Chip>
          {types.map(t => (
            <Chip key={t} selected={type === t} onClick={() => setType(type === t ? null : t)} count={counts[t]}>{MOTION_TYPE_SHORT[t] || t}</Chip>
          ))}
        </div>
      )}
      <div style={{ fontSize: font.small, color: color.textSecondary }}>הצעות לסדר היום שהועברו לדיון בוועדה</div>
      <Card padding={0}>
        {visible.map((m, i) => (
          <AgendaMotionRow key={m.id} motion={m} members={data.members} committees={data.committees} hideCommittee divider={i > 0} from={from} />
        ))}
      </Card>
      {visible.length < shown.length && (
        <Button fullWidth onClick={() => setVisibleCount(n => n + PAGE)}>
          הצגת עוד ({(shown.length - visible.length).toLocaleString('he-IL')} נותרו)
        </Button>
      )}
    </div>
  );
}
